import { ReactNode, useEffect, useState } from 'react';
import { SearchRoleType } from '@/api/v1/organization/users/getSearchUsersApi';
import { Maybe, ResponseErrorType } from '@/types/sharedTypes';
import { useQuery } from '@tanstack/react-query';
import { searchUsers } from '@/bundle/shared/api';
import { UserAssignmentStatusCodeType } from '@/const/shared';
import { EMAIL } from '@/const/regExpPatterns';
import {
  NewUserNotification,
  USER_ASSIGNMENT_NOTIFICATIONS_MAP,
} from '@/bundle/shared/components/UserAssignmentNotifications';
import { UserEmailInput } from './ui/UserEmailInput';
import { SearchUserType } from '@/api/v1/recipient/getSearchUsersByProxyApi';

type SearchUserEmailInputType = {
  role: SearchRoleType;
  email: string;
  onSearch: (userId: null | string) => void;
  onSubmit: () => void;
  assignmentStatus: UserAssignmentStatusCodeType;
  error?: string;
  apiError?: ResponseErrorType;
  disabled?: boolean;
};

export const getSearchUserId = (searchUsers: SearchUserType[]) => {
  return searchUsers?.[0]?.id || null;
};

export const SearchUserEmailInput = ({
  role,
  email,
  error,
  apiError,
  onSearch,
  onSubmit,
  assignmentStatus,
  disabled,
}: SearchUserEmailInputType) => {
  const [notification, setNotification] = useState<Maybe<ReactNode>>(null);
  const shouldLoadUsers = EMAIL.test(email);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['search_user', email, role],
    queryFn: () => {
      return searchUsers(email, role);
    },
    enabled: false,
    gcTime: 0,
  });

  const getAssignNotification = (statusCode: UserAssignmentStatusCodeType) => {
    if (!data?.body) return;

    const userId = !!getSearchUserId(data?.body?.results) && EMAIL.test(email);

    if (!userId) {
      return <NewUserNotification />;
    }

    return USER_ASSIGNMENT_NOTIFICATIONS_MAP[statusCode];
  };

  // TODO: think about how to catch only case when request is sent
  useEffect(() => {
    if (!data?.body) {
      setNotification(null);

      return;
    }

    const userId = getSearchUserId(data?.body?.results);
    const assignNotification = getAssignNotification(assignmentStatus);

    setNotification(assignNotification);
    onSearch(userId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.body?.results, assignmentStatus]);

  const checkEmail = () => {
    onSubmit();

    if (shouldLoadUsers) {
      refetch();
    }
  };

  return (
    <UserEmailInput
      notification={notification}
      onSubmit={checkEmail}
      isLoading={isFetching}
      error={error}
      apiError={apiError}
      disabled={disabled}
    />
  );
};
